import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

const isPlaceholderEmail = (email) => (email || '').toLowerCase().endsWith('@otp.skcart.local');

function ProfileAccountSection() {
    const {
        user,
        updateProfile,
        refreshProfile,
        profilePhoneRequestOtp,
        profilePhoneVerifyOtp,
        profileEmailRequestOtp,
        profileEmailVerifyOtp,
    } = useAuth();

    const [userName, setUserName] = useState(user?.UserName || '');
    const [phone, setPhone] = useState(user?.Phone || '');
    const [email, setEmail] = useState(isPlaceholderEmail(user?.Email) ? '' : (user?.Email || ''));
    const [phoneOtp, setPhoneOtp] = useState('');
    const [emailOtp, setEmailOtp] = useState('');
    const [phoneOtpRequested, setPhoneOtpRequested] = useState(false);
    const [emailOtpRequested, setEmailOtpRequested] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const loadProfile = async () => {
            const result = await refreshProfile();
            if (cancelled || !result.success || !result.data) {
                return;
            }
            const profile = result.data;
            setUserName(profile.UserName || '');
            setPhone(profile.Phone || '');
            setEmail(isPlaceholderEmail(profile.Email) ? '' : (profile.Email || ''));
        };
        loadProfile();
        return () => {
            cancelled = true;
        };
    }, [refreshProfile]);

    const handleSaveProfile = async (event) => {
        event.preventDefault();
        setError('');
        setMessage('');
        if (newPassword && newPassword !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        setIsSaving(true);
        const result = await updateProfile({
            userName,
            currentPassword: currentPassword || undefined,
            newPassword: newPassword || undefined,
        });
        setIsSaving(false);
        if (!result.success) {
            setError(result.message);
            return;
        }
        setMessage('Profile saved.');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
    };

    const handlePhoneOtpRequest = async () => {
        setError('');
        const result = await profilePhoneRequestOtp({ phone });
        if (!result.success) {
            setError(result.message);
            return;
        }
        setMessage('OTP sent to your mobile.');
        setPhoneOtpRequested(true);
    };

    const handlePhoneOtpVerify = async () => {
        setError('');
        const result = await profilePhoneVerifyOtp({ phone, otp: phoneOtp });
        if (!result.success) {
            setError(result.message);
            return;
        }
        setMessage('Mobile verified.');
        setPhoneOtp('');
        setPhoneOtpRequested(false);
    };

    const handleEmailOtpRequest = async () => {
        setError('');
        const result = await profileEmailRequestOtp({ email });
        if (!result.success) {
            setError(result.message);
            return;
        }
        setMessage('OTP sent to your email.');
        setEmailOtpRequested(true);
    };

    const handleEmailOtpVerify = async () => {
        setError('');
        const result = await profileEmailVerifyOtp({ email, otp: emailOtp });
        if (!result.success) {
            setError(result.message);
            return;
        }
        setMessage('Email verified.');
        setEmailOtp('');
        setEmailOtpRequested(false);
    };

    const normalizePhone = (value) => String(value || '').replace(/\D/g, '').slice(-10);
    const isVerifiedFlag = (value) => Number(value) === 1;
    const phoneVerified = isVerifiedFlag(user?.IsPhoneVerified) && normalizePhone(phone) === normalizePhone(user?.Phone);
    const emailVerified = isVerifiedFlag(user?.IsEmailVerified)
        && !isPlaceholderEmail(user?.Email)
        && email.trim().toLowerCase() === String(user?.Email || '').trim().toLowerCase();
    const showPhoneOtp = !phoneVerified && phoneOtpRequested;
    const showEmailOtp = !emailVerified && emailOtpRequested;

    return (
        <div className="card">
            <div className="card-header">
                <h5>Account Details</h5>
            </div>
            <div className="card-body">
                {message ? <p className="text-success">{message}</p> : null}
                {error ? <p className="text-danger">{error}</p> : null}
                <form onSubmit={handleSaveProfile}>
                    <div className="form-group">
                        <label>User name</label>
                        <input className="form-control" value={userName} onChange={(e) => setUserName(e.target.value)} required />
                    </div>
                    <div className="border rounded p-3 mb-20">
                        <h6 className={phoneVerified ? 'text-success' : undefined}>Verify mobile</h6>
                        <p className={`small mb-10 ${phoneVerified ? 'text-success' : 'text-muted'}`}>
                            {phoneVerified ? `Verified: ${user.Phone}` : 'Verify mobile to login with OTP or password using your number.'}
                        </p>
                        <div className="form-group">
                            <input
                                className="form-control mb-10"
                                type="tel"
                                value={phone}
                                onChange={(event) => {
                                    setPhone(event.target.value);
                                    setPhoneOtp('');
                                    setPhoneOtpRequested(false);
                                }}
                                placeholder="Mobile number"
                            />
                        </div>
                        {!phoneVerified ? (
                            <div className="d-flex gap-2 flex-wrap mb-10">
                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handlePhoneOtpRequest}>Send OTP</button>
                            </div>
                        ) : null}
                        {showPhoneOtp ? (
                            <>
                                <div className="form-group">
                                    <input className="form-control" value={phoneOtp} onChange={(e) => setPhoneOtp(e.target.value)} placeholder="Mobile OTP" maxLength={6} />
                                </div>
                                <button type="button" className="btn btn-sm btn-fill-out" onClick={handlePhoneOtpVerify}>Verify mobile</button>
                            </>
                        ) : null}
                    </div>
                    <div className="border rounded p-3 mb-20">
                        <h6 className={emailVerified ? 'text-success' : undefined}>Verify email</h6>
                        <p className={`small mb-10 ${emailVerified ? 'text-success' : 'text-muted'}`}>
                            {emailVerified ? `Verified: ${user.Email}` : 'Verify email to login with email OTP or reset password by email.'}
                        </p>
                        <div className="form-group">
                            <input
                                className="form-control mb-10"
                                type="email"
                                value={email}
                                onChange={(event) => {
                                    setEmail(event.target.value);
                                    setEmailOtp('');
                                    setEmailOtpRequested(false);
                                }}
                                placeholder="Email address"
                            />
                        </div>
                        {!emailVerified ? (
                            <div className="d-flex gap-2 flex-wrap mb-10">
                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleEmailOtpRequest}>Send OTP</button>
                            </div>
                        ) : null}
                        {showEmailOtp ? (
                            <>
                                <div className="form-group">
                                    <input className="form-control" value={emailOtp} onChange={(e) => setEmailOtp(e.target.value)} placeholder="Email OTP" maxLength={6} />
                                </div>
                                <button type="button" className="btn btn-sm btn-fill-out" onClick={handleEmailOtpVerify}>Verify email</button>
                            </>
                        ) : null}
                    </div>
                    <div className="form-group">
                        <label>Current password (optional when setting first password)</label>
                        <input className="form-control" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label>New password</label>
                        <input className="form-control" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label>Confirm password</label>
                        <input className="form-control" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
                    </div>
                    <button type="submit" className="btn btn-fill-out" disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save profile'}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default ProfileAccountSection;
